import React from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Mention } from 'primereact/mention';
import { Button } from 'primereact/button';
import { Image } from 'primereact/image';
import { FileUpload } from 'primereact/fileupload';
import { FORMATTED_TAG_SUGGESTIONS } from './constant';
import { Divider } from 'primereact/divider';
import { Field, FormikProps, useFormikContext } from 'formik';
import { Dropdown } from 'primereact/dropdown';
import { SHOP_TYPES } from '../../constants/shopTypes';

const ArticleMetadata = ({
  toastRef,
  compact = false
}) => {
  const formik = useFormikContext();
  const [suggestions, setSuggestions] = React.useState([]);

  // Convert shopType values to clean labels
  const businesses = React.useMemo(() => {
    const shopType = formik.values?.shopType;
    if (!shopType) return [];

    // If shopType is an array of values, convert them to labels
    if (Array.isArray(shopType)) {
      return shopType.map(type => {
        // Find the matching item in SHOP_TYPES
        const item = SHOP_TYPES.flatMap(category => category.items)
          .find(item => item.value === type);
        return item ? item.label : type;
      });
    }

    // If shopType is an object, flatten it and convert values to labels
    return Object.values(shopType)
      .flat()
      .flatMap(category => Object.values(category))
      .map(type => {
        const item = SHOP_TYPES.flatMap(category => category.items)
          .find(item => item.value === type);
        return item ? item.label : type;
      });
  }, [formik.values?.shopType]);

  const handleTagSearch = (event, formik) => {
    const trigger = event.trigger;

    if (trigger === '#') {
      setTimeout(() => {
        const query = event.query;
        let suggestions;

        if (!query.trim().length) {
          suggestions = [...FORMATTED_TAG_SUGGESTIONS];
        } else {
          suggestions = FORMATTED_TAG_SUGGESTIONS.filter((tag) => {
            return tag.label.toLowerCase().startsWith(query.toLowerCase());
          });
        }

        setSuggestions(suggestions);
      }, 250);
    }
  };

  const tagItemTemplate = (tag, options) => {
    const trigger = options.trigger;

    if (trigger === '#' && tag.label) {
      return (
        <span className="flex align-items-center gap-2">
          <i className="pi pi-tag text-sm"></i>
          <span className="font-medium">{tag.label}</span>
          <span className="text-sm text-gray-500">({tag.category})</span>
        </span>
      );
    }

    return null;
  };

  const handleTagSelect = (event, formik) => {
    try {
      if (!event || !event.value) return;

      const selectedTag = event.value;
      const currentTags = formik.values.tags || [];

      // Handle both string and object values
      const tagToAdd = typeof selectedTag === 'string'
        ? selectedTag
        : selectedTag && selectedTag.label
          ? selectedTag.label
          : selectedTag;

      if (!currentTags.includes(tagToAdd)) {
        currentTags.push(tagToAdd);
      }
      formik.setFieldValue('tags', currentTags);
      formik.setFieldValue('suggestions', []);
    } catch (error) {
      console.error('Error handling tag selection:', error);
      // Fallback to adding the raw value if there's an error
      const currentTags = formik.values.tags || [];
      if (!currentTags.includes(event.value)) {
        currentTags.push(event.value);
      }
      formik.setFieldValue('tags', currentTags);
      formik.setFieldValue('suggestions', []);
    }
  };

  return (
    <div className="flex flex-column gap-3">
        {/* Thumbnail */}
        <div className="field">
        <div className="flex flex-column gap-3">
        {(formik.values.thumbnail || formik.values._thumbnailPreview) && (
            <div className="relative w-full border-round overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
              <div className="absolute top-0 left-0 w-full h-full flex align-items-center justify-content-center bg-gray-100">
                <Image
                  src={formik.values.thumbnail?.url || formik.values._thumbnailPreview?.url}
                  alt="Thumbnail preview"
                  className="w-full h-full"
                  preview
                  imageStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.src = '/placeholder-image.png';
                  }}
                />
                <Button
                  type="button"
                  icon="pi pi-times"
                  className="p-button-rounded p-button-danger p-button-sm absolute"
                  style={{ top: '-0.5rem', right: '-0.5rem' }}
                  onClick={() => {
                    formik.setFieldValue('thumbnail', null);
                    formik.setFieldValue('_thumbnailPreview', null);
                  }}
                />
              </div>
            </div>
          )}
          <FileUpload
            mode="basic"
            name="thumbnail"
            accept="image/*"
            auto
            maxFileSize={2000000}
            multiple
            onSelect={(e) => {
              const file = e.files[0];
              if (file) {
                // Just store the File object directly - it will be processed by processArticleImages
                formik.setFieldValue('thumbnail', file);
                // For preview, we still need to create an object URL
                formik.setFieldValue('_thumbnailPreview', {
                  url: URL.createObjectURL(file),
                  name: file.name
                });
              }
            }}
            chooseLabel={formik.values.thumbnail ? 'Change Thumbnail' : 'Choose Thumbnail'}
            className="w-full p-button-primary"
          />
        </div>
        <small className="text-500 block mt-2">
          {formik.values.thumbnail || formik.values._thumbnailPreview ?
            'Thumbnail for your article' :
            'Upload a thumbnail image for this article (max 2MB)'}
        </small>
        {formik.errors.thumbnail && formik.touched.thumbnail && (
          <small className="p-error block mt-1">{formik.errors.thumbnail}</small>
        )}
      </div>

      {/* Title */}
      <div className="field">
        <label htmlFor="title" className="block font-medium">Title</label>
        <Field
          name="title"
          as={InputText}
          className="w-full"
          placeholder="Enter article title"
          required
        />
        {formik.errors.title && formik.touched.title && (
          <small className="p-error">{formik.errors.title}</small>
        )}
      </div>

      {/* Byline */}
      <div className="field">
        <label htmlFor="byline" className="block font-medium">Author</label>
        <Field
          name="byline"
          as={InputText}
          className="w-full"
          placeholder="Enter article author"
          required
        />
        {formik.errors.byline && formik.touched.byline && (
          <small className="p-error">{formik.errors.byline}</small>
        )}
      </div>

      {/* Introduction */}
      <div className="field">
        <label htmlFor="introduction" className="block font-medium">Introduction</label>
        <Field
          name="introduction"
          as={InputTextarea}
          rows={4}
          className="w-full"
          placeholder="Enter article introduction"
          required
        />
        {formik.errors.introduction && formik.touched.introduction && (
          <small className="p-error">{formik.errors.introduction}</small>
        )}
      </div>

      {/* Tags (using Mention) */}
      <div className="field">
        <label className="block font-medium">Tags</label>
        <Field
          name="tags"
          validate={(value) => {
            if (!value || value.length === 0) {
              return 'At least one tag is required';
            }
            return;
          }}
          render={({ field, form }) => (
            <div>
              <Mention
                value={(form.values.tags || []).map(tag => `#${tag}`).join(' ')}
                onChange={(e) => {
                  // Get the current input value
                  const inputValue = e.target.value;

                  // Check if the last character is # and there's existing content
                  if (inputValue.endsWith('#') && inputValue.length > 1) {
                    // Insert comma and space before the last #
                    const lastIndex = inputValue.lastIndexOf('#');
                    const newValue = inputValue.slice(0, lastIndex) + ', #';

                    // Update the input value with comma and space
                    e.target.value = newValue;

                    // Process the value for form state
                    const processedValue = newValue
                      .split(/[\s,]+/)
                      .map(tag => tag.trim())
                      .filter(Boolean)
                      .map(tag => tag.startsWith('#') ? tag : `#${tag}`)
                      .join(' ');

                    const newTags = processedValue
                      .split(' ')
                      .filter(tag => tag.startsWith('#'))
                      .map(tag => tag.slice(1));

                    form.setFieldValue('tags', newTags);
                    return;
                  }

                  // Process the input value to ensure tags start with #
                  const processedValue = inputValue
                    .split(/[\s,]+/)
                    .map(tag => tag.trim())
                    .filter(Boolean)
                    .map(tag => tag.startsWith('#') ? tag : `#${tag}`)
                    .join(' ');

                  // Update the form with the cleaned tags
                  const newTags = processedValue
                    .split(' ')
                    .filter(tag => tag.startsWith('#'))
                    .map(tag => tag.slice(1));

                  form.setFieldValue('tags', newTags);
                }}
                trigger={['#']}
                suggestions={Array.isArray(suggestions) && suggestions.length ? suggestions : undefined}
                onSearch={(e) => handleTagSearch(e, form)}
                field="label"
                placeholder="Enter # to mention tags"
                itemTemplate={tagItemTemplate}
                rows={4}
                cols={40}
                onSelect={(e) => handleTagSelect(e, form)}
              />
                <small className="text-500 block mt-2">Input <span className="font-medium">#</span> to add a new tag. you can select from suggestions or keep typing to add your own.</small>
              </div>
          )}
        />
        {formik.errors.tags && formik.touched.tags && (
          <small className="p-error">{formik.errors.tags}</small>
        )}
      </div>
      {/* Business */}
      <div className="field">
        <label htmlFor="business" className="block font-medium">Business</label>
        <Field
          name="business"
          as={Dropdown}
          options={businesses}
          optionLabel="shopName"
          placeholder="Select a business"
          className="w-full"
          required
        />
        {formik.errors.business && formik.touched.business && (
          <small className="p-error">{formik.errors.business}</small>
        )}
      </div>

      <Divider />
    </div>
  );
};

export default ArticleMetadata;
